import {
  createElement,
  useMemo,
  type CSSProperties,
  type ReactNode,
} from 'react';
import PowerMention from '@/components/help-center/powers/PowerMention';
import AceMention from '@/components/help-center/aces/AceMention';
import { PowerGalleryBbcodeEmbed } from '@/components/help-center/powers/PowerGalleryBbcodeEmbed';
import {
  splitTextWithPowerMentions,
  type BracketEmbedTextPart,
} from '@/lib/power-mention-parse';
import { decodeHtmlEntities } from '@/lib/cms-html';
import { cn } from '@/lib/utils';

interface PowerMentionTextProps {
  text: string;
  className?: string;
  mentionClassName?: string;
  as?: 'p' | 'span';
}

function renderBracketPart(
  part: BracketEmbedTextPart,
  key: string | number,
  mentionClassName?: string
): ReactNode {
  if (part.type === 'mention') {
    return (
      <PowerMention key={key} slug={part.slug} className={mentionClassName}>
        {part.label}
      </PowerMention>
    );
  }
  if (part.type === 'ace') {
    return (
      <AceMention key={key} slug={part.slug} className={mentionClassName}>
        {part.label}
      </AceMention>
    );
  }
  if (part.type === 'smilies') {
    return <PowerGalleryBbcodeEmbed key={key} kind="smilies" slug={part.slug} />;
  }
  if (part.type === 'pawns') {
    return <PowerGalleryBbcodeEmbed key={key} kind="pawns" slug={part.slug} />;
  }
  return <span key={key}>{decodeHtmlEntities(part.value)}</span>;
}

export default function PowerMentionText({
  text,
  className,
  mentionClassName,
  as = 'span',
}: PowerMentionTextProps) {
  const parts = useMemo(() => splitTextWithPowerMentions(text), [text]);
  const Tag = as;

  if (!text.trim()) return null;

  const hasBlock = parts.some((p) => p.type === 'smilies' || p.type === 'pawns');
  if (hasBlock) {
    return (
      <div className={className}>
        {parts.map((part, index) => renderBracketPart(part, index, mentionClassName))}
      </div>
    );
  }

  return (
    <Tag className={className}>
      {parts.map((part, index) => renderBracketPart(part, index, mentionClassName))}
    </Tag>
  );
}

interface PowerMentionHtmlProps {
  html: string;
  className?: string;
  mentionClassName?: string;
}

function parseInlineStyle(style: string): CSSProperties {
  const out: Record<string, string> = {};
  for (const chunk of style.split(';')) {
    const idx = chunk.indexOf(':');
    if (idx <= 0) continue;
    const rawKey = chunk.slice(0, idx).trim();
    const rawVal = chunk.slice(idx + 1).trim();
    if (!rawKey || !rawVal) continue;
    const key = rawKey.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    out[key] = rawVal;
  }
  return out as CSSProperties;
}

function domAttrsToProps(el: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on') || name === 'srcdoc') continue;
    if (name === 'class') {
      props.className = attr.value;
      continue;
    }
    if (name === 'style') {
      props.style = parseInlineStyle(attr.value);
      continue;
    }
    if (name === 'for') {
      props.htmlFor = attr.value;
      continue;
    }
    if (name === 'colspan' || name === 'rowspan' || name === 'tabindex') {
      const n = Number(attr.value);
      props[name] = Number.isFinite(n) ? n : attr.value;
      continue;
    }
    props[attr.name] = attr.value;
  }
  return props;
}

const BLOCKED_TAGS = new Set(['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style']);
const LI_UNWRAP_TAGS = new Set(['p', 'div']);

function walkTextNode(text: string, key: string, mentionClassName?: string): ReactNode {
  if (!text) return null;
  const parts = splitTextWithPowerMentions(text);
  if (parts.length === 1 && parts[0].type === 'text') {
    return decodeHtmlEntities(parts[0].value);
  }
  return parts.map((part, i) => renderBracketPart(part, `${key}-${i}`, mentionClassName));
}

/** contentEditable / editores costumam envolver o texto do <li> num <p> ou <div>. */
function unwrapListItemChildren(el: Element): Node[] {
  const kids = Array.from(el.childNodes);
  if (kids.length !== 1 || kids[0].nodeType !== Node.ELEMENT_NODE) return kids;
  const only = kids[0] as Element;
  if (!LI_UNWRAP_TAGS.has(only.tagName.toLowerCase())) return kids;
  return Array.from(only.childNodes);
}

function walkDomNode(node: Node, key: string, mentionClassName?: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return walkTextNode(node.textContent ?? '', key, mentionClassName);
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (BLOCKED_TAGS.has(tag)) return null;

  const rawChildren = tag === 'li' ? unwrapListItemChildren(el) : Array.from(el.childNodes);
  const childNodes = rawChildren
    .map((child, i) => walkDomNode(child, `${key}.${i}`, mentionClassName))
    .flat()
    .filter((n) => n != null && n !== false && n !== '');

  const props = { ...domAttrsToProps(el), key };

  // Void elements
  if (tag === 'br' || tag === 'hr' || tag === 'img' || tag === 'input') {
    return createElement(tag, props);
  }

  return createElement(tag, props, ...childNodes);
}

/** Converte HTML do CMS em React, preservando <ul>/<li> e injectando menções nos text nodes. */
function htmlToMentionNodes(html: string, mentionClassName?: string): ReactNode[] {
  if (typeof DOMParser === 'undefined') {
    return [<span key="ssr" dangerouslySetInnerHTML={{ __html: html }} />];
  }

  const doc = new DOMParser().parseFromString(`<div id="__pm_root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__pm_root');
  if (!root) return [];

  return Array.from(root.childNodes)
    .map((node, i) => walkDomNode(node, `n${i}`, mentionClassName))
    .flat()
    .filter((n) => n != null && n !== false && n !== '');
}

/** Listas estáveis: bullets via ::before evitam o bug “marcador numa linha, texto na outra”. */
export const RICH_HTML_LIST_CLASS = cn(
  '[&_ul]:my-2 [&_ul]:list-none [&_ul]:space-y-1.5 [&_ul]:pl-0',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:space-y-1.5 [&_ol]:pl-5',
  '[&_ul>li]:relative [&_ul>li]:pl-5 [&_ul>li]:leading-relaxed',
  '[&_ul>li]:before:absolute [&_ul>li]:before:left-1.5 [&_ul>li]:before:top-0 [&_ul>li]:before:content-["•"]',
  '[&_ol>li]:leading-relaxed [&_ol>li]:pl-1',
  '[&_li>p]:my-0 [&_li>div]:my-0',
  '[&_p]:my-2 [&_a]:underline'
);

export function PowerMentionHtml({ html, className, mentionClassName }: PowerMentionHtmlProps) {
  const nodes = useMemo(
    () => htmlToMentionNodes(html, mentionClassName),
    [html, mentionClassName]
  );

  if (!html.trim()) return null;

  return <div className={cn(className, RICH_HTML_LIST_CLASS)}>{nodes}</div>;
}

export function PowerMentionParagraph({
  html,
  className,
  asPlainParagraph = true,
}: {
  html: string;
  className?: string;
  asPlainParagraph?: boolean;
}) {
  const t = html.trim();
  if (!t) return null;

  if (asPlainParagraph) {
    return (
      <p className={cn('text-gray-700 leading-relaxed', className)}>
        <PowerMentionText text={html} as="span" />
      </p>
    );
  }

  return <PowerMentionText text={html} className={className} as="span" />;
}

function looksLikeRichHtml(text: string): boolean {
  return /<[a-z][^>]*>/i.test(text);
}

/** Texto ou HTML rico das descrições de power (com menções e embeds). */
export function PowerDescriptionContent({
  text,
  className,
  as = 'span',
}: {
  text: string;
  className?: string;
  as?: 'p' | 'span';
}) {
  if (!text.trim()) return null;
  if (looksLikeRichHtml(text)) {
    return <PowerMentionHtml html={text} className={className} />;
  }
  return <PowerMentionText text={text} className={className} as={as} />;
}
